'use client';

import {
  Children,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';
import s from './landing.module.css';

interface SwipeRailProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** Use `ol` when the items are ordered steps. */
  as?: 'div' | 'ol';
  /** Classes for the position dots, for example a text color. */
  dotsClassName?: string;
  /** Card a phone sees first, such as the most popular plan. Neighbours
      still peek in on both sides. */
  initialIndex?: number;
}

/**
 * Lays its children out in the page's grid on larger screens and as a
 * horizontal, snapping card rail with position dots on phones, which keeps
 * long card sections to about one screen of vertical scrolling.
 */
export function SwipeRail({
  children,
  as: Tag = 'div',
  className,
  dotsClassName,
  initialIndex = 0,
  ...rest
}: SwipeRailProps) {
  const railRef = useRef<HTMLElement | null>(null);
  const count = Children.count(children);
  const [active, setActive] = useState(initialIndex);

  // Before first paint, so the rail never visibly jumps. Grids on larger
  // screens do not scroll and are left alone.
  useLayoutEffect(() => {
    const rail = railRef.current;
    const item = rail?.children[initialIndex] as HTMLElement | undefined;
    if (!rail || !item || rail.scrollWidth <= rail.clientWidth) return;
    const padding = Number.parseFloat(
      getComputedStyle(rail).paddingLeft || '0'
    );
    rail.scrollLeft = item.offsetLeft - padding;
  }, [initialIndex]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const items = Array.from(rail.children) as HTMLElement[];
        const start =
          rail.scrollLeft +
          Number.parseFloat(getComputedStyle(rail).paddingLeft || '0');
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        items.forEach((item, index) => {
          const gap = Math.abs(item.offsetLeft - start);
          if (gap < distance) {
            distance = gap;
            nearest = index;
          }
        });
        setActive(nearest);
      });
    };
    rail.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      rail.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <>
      <Tag
        ref={(element: HTMLElement | null) => {
          railRef.current = element;
        }}
        className={cn(s.rail, className)}
        {...rest}
      >
        {children}
      </Tag>
      <div className={cn(s.railDots, dotsClassName)} aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <span key={index} data-active={index === active || undefined} />
        ))}
      </div>
    </>
  );
}
